import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import logo from "../images/logo.png";
import { isLoggedIn, logout, getStoredCustomer, fetchMe } from "../services/authService";
import {
  FaUserCircle,
  FaSignOutAlt,
  FaClipboardList,
  FaThLarge,
  FaFileInvoiceDollar,
  FaChevronDown,
  FaSignInAlt,
  FaCalendarPlus,
} from "react-icons/fa";
import "../style/websiteNavbar.css";

const WebsiteNavbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const isHome = location.pathname === "/" || location.pathname === "/home";
  const isMyAccount = location.pathname === "/my-account";
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const checkAuth = () => {
    const isAuth = isLoggedIn();
    setLoggedIn(isAuth);
    if (isAuth) {
      const stored = getStoredCustomer();
      if (stored) {
        setCustomer(stored);
      }
      fetchMe().then((res) => {
        if (res?.success && res.customer) {
          setCustomer(res.customer);
        }
      }).catch(() => {});
    } else {
      setCustomer(null);
    }
  };

  useEffect(() => {
    checkAuth();
    window.addEventListener("jb_auth_change", checkAuth);
    window.addEventListener("storage", checkAuth);

    const handleScroll = () => {
      if (window.scrollY > 30) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("jb_auth_change", checkAuth);
      window.removeEventListener("storage", checkAuth);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [location.pathname]);

  // Close dropdown on route change or click outside
  useEffect(() => {
    setDropdownOpen(false);
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    setLoggedIn(false);
    setCustomer(null);
    setMenuOpen(false);
    setDropdownOpen(false);
    navigate("/");
  };

  const displayName = customer?.first_name || "Account";

  return (
    <header className={`jb-navbar-wrapper ${menuOpen ? "jb-nav-menu-open" : (isHome ? (scrolled ? "jb-nav-scrolled" : "jb-nav-transparent") : "jb-nav-solid")}`}>
      <div className="jb-navbar-container">
        {/* BRAND LOGO */}
        <div className="jb-nav-brand" onClick={() => navigate("/")}>
          <div className="jb-logo-frame">
            <img src={logo} alt="Jerry's Bins Logo" className="jb-logo-img" />
          </div>
          <div className="jb-brand-text">
            <span className="jb-brand-name">JERRY'S<span className="jb-brand-accent"> BINS</span></span>
            <span className="jb-brand-sub">Waste & Skip Hire</span>
          </div>
        </div>

        {/* MOBILE HAMBURGER BUTTON (Logged-out Only) */}
        {!loggedIn && (
          <button
            className={`jb-hamburger ${menuOpen ? "open" : ""}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle navigation menu"
          >
            <span />
            <span />
            <span />
          </button>
        )}

        {/* NAVIGATION LINKS (Public / Logged-out Only) */}
        {!loggedIn && (
          <nav className={`jb-nav-links-wrap ${menuOpen ? "open" : ""}`}>
            <ul className="jb-nav-menu">
              <li>
                <NavLink to="/" end className={({ isActive }) => (isActive ? "jb-link active" : "jb-link")} onClick={() => setMenuOpen(false)}>
                  Home
                </NavLink>
              </li>
              <li>
                <NavLink to="/services" className={({ isActive }) => (isActive ? "jb-link active" : "jb-link")} onClick={() => setMenuOpen(false)}>
                  Services
                </NavLink>
              </li>
              <li>
                <NavLink to="/about-us" className={({ isActive }) => (isActive ? "jb-link active" : "jb-link")} onClick={() => setMenuOpen(false)}>
                  About Us
                </NavLink>
              </li>
              <li>
                <NavLink to="/commercial" className={({ isActive }) => (isActive ? "jb-link active" : "jb-link")} onClick={() => setMenuOpen(false)}>
                  Commercial
                </NavLink>
              </li>
              <li>
                <NavLink to="/contact" className={({ isActive }) => (isActive ? "jb-link active" : "jb-link")} onClick={() => setMenuOpen(false)}>
                  Contact
                </NavLink>
              </li>
              <li>
                <NavLink to="/faq" className={({ isActive }) => (isActive ? "jb-link active" : "jb-link")} onClick={() => setMenuOpen(false)}>
                  FAQ
                </NavLink>
              </li>
            </ul>

            {/* MOBILE ONLY ACTIONS */}
            <div className="jb-mobile-actions">
              <NavLink to="/loginpage" className="jb-mobile-btn jb-mobile-login-btn" onClick={() => setMenuOpen(false)}>
                <FaSignInAlt /> Login / Signup
              </NavLink>
              <NavLink to="/booking" className="jb-mobile-btn jb-mobile-book-btn" onClick={() => setMenuOpen(false)}>
                <FaCalendarPlus /> Book Now
              </NavLink>
            </div>
          </nav>
        )}

        {/* ACTIONS / PROFILE */}
        <div className={`jb-nav-actions ${loggedIn ? "logged-in-actions" : ""}`}>
          {loggedIn ? (
            <div className="jb-user-menu-rel" ref={dropdownRef}>
              <button
                className={`jb-user-badge-btn ${dropdownOpen ? "active" : ""} ${isMyAccount ? "jb-badge-static" : ""}`}
                onClick={() => {
                  if (!isMyAccount) {
                    setDropdownOpen(!dropdownOpen);
                  }
                }}
                aria-label="User Account Menu"
                style={isMyAccount ? { cursor: "default" } : {}}
              >
                <div className="jb-avatar-icon">
                  {customer?.first_name ? customer.first_name[0].toUpperCase() : <FaUserCircle />}
                </div>
                <span className="jb-user-btn-name">{displayName}</span>
                {!isMyAccount && (
                  <FaChevronDown className={`jb-caret ${dropdownOpen ? "rotate" : ""}`} />
                )}
              </button>

              {dropdownOpen && (
                <div className="jb-dropdown-popover">
                  <div className="jb-dropdown-user-info">
                    <p className="jb-user-full-name">
                      {customer ? `${customer.first_name} ${customer.last_name || ""}`.trim() : "Member"}
                    </p>
                    <p className="jb-user-email-text">{customer?.email || ""}</p>
                  </div>

                  <div className="jb-dropdown-links">
                    <NavLink
                      to="/my-account?tab=overview"
                      className="jb-dropdown-link"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <FaThLarge className="jb-dropdown-icon" /> Dashboard Overview
                    </NavLink>
                    <NavLink
                      to="/my-account?tab=bookings"
                      className="jb-dropdown-link"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <FaClipboardList className="jb-dropdown-icon" /> My Bookings
                    </NavLink>
                    <NavLink
                      to="/my-account?tab=invoices"
                      className="jb-dropdown-link"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <FaFileInvoiceDollar className="jb-dropdown-icon" /> Tax Invoices
                    </NavLink>
                  </div>

                  <div className="jb-dropdown-footer">
                    <button className="jb-dropdown-logout-btn" onClick={handleLogout}>
                      <FaSignOutAlt /> Log Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <NavLink to="/loginpage" className="jb-nav-login-link">
              LOGIN
            </NavLink>
          )}

          <NavLink to="/booking" className="jb-nav-book-cta">
            <span>BOOK NOW</span>
          </NavLink>
        </div>
      </div>

      {menuOpen && <div className="jb-nav-backdrop" onClick={() => setMenuOpen(false)} />}
    </header>
  );
};

export default WebsiteNavbar;